import ModalForm from 'assets/components/form/modalForm/ModalForm';
import ThemeModalFormDefault from 'assets/components/form/modalForm/themes/Theme.ModalForm.Default';
import { getLocales } from 'assets/locales/Locale';
import { useSalesContractProvider } from '../Sales.Contract.Hooks';
import CancellationReason from 'models/enums/CancellationReason';
import contractSetStatusApi from 'models/sales/contract/setStatus/Api.Contract.SetStatus';
import ContractStatusCode from 'models/enums/ContractStatusCode';
import { HttpStatus } from 'config/Api.Config';
import ContractCancellationReason from './cancellation/Contract.CancellationReason';
import ContractCancellationRefund from './cancellation/Contract.CancellationRefund';
import { useEffect, useState } from 'react';
import contractSetStatusUnrestrictedApi from 'models/sales/contract/setStatusUnrestricted/Api.Contract.SetStatusUnrestricted';
import { addLog } from 'assets/components/feedback/Feedback';

export type CancellationForm = Api.Core.Contract.SetStatus.IRequest;

export enum Cancellation {
  REASON = 'reason',
  REFUND = 'refund',
}

export default function ContractCancellation() {
  const { lang } = getLocales();
  const { selected, pageRouter, listApi, setIsStatusChanging } = useSalesContractProvider();

  const [currentTab, setCurrentTab] = useState<Cancellation | undefined>(Cancellation.REASON);

  const CancellationTabs: Array<Template.ModalForm.Tab<CancellationForm>> = [
    { id: Cancellation.REASON, text: lang.details },
    { id: Cancellation.REFUND, text: lang.refund },
  ];

  useEffect(() => {
    if (!currentTab) {
      setCurrentTab(Cancellation.REASON);
    }
  }, [currentTab]);

  const validateForm = (data: Partial<CancellationForm>, errors: Hooks.Form.Errors<CancellationForm>) => {
    errors.contractCancelationRequest = errors.contractCancelationRequest || {};

    if (currentTab === Cancellation.REASON) {
      if (!data.contractCancelationRequest?.cancellationDate) {
        errors.contractCancelationRequest.cancellationDate = lang.mustNotBeEmpty;
      }
      if (!data.contractCancelationRequest?.cancellationOdometerReading) {
        errors.contractCancelationRequest.cancellationOdometerReading = lang.mustNotBeEmpty;
      }
      if (!data.contractCancelationRequest?.cancellationReason) {
        errors.contractCancelationRequest.cancellationReason = lang.mustNotBeEmpty;
      }
      if (
        data.contractCancelationRequest?.cancellationReason === CancellationReason.OTHER &&
        !data.contractCancelationRequest?.cancellationReasonDescription
      ) {
        errors.contractCancelationRequest.cancellationReasonDescription = lang.mustNotBeEmpty;
      }
    }

    if (currentTab === Cancellation.REFUND) {
      if (!data.contractCancelationRequest?.refundDateAtLocal) {
        errors.contractCancelationRequest.refundDateAtLocal = lang.mustNotBeEmpty;
      }
    }
  };

  function onExecuted(res: Api.Res<void>) {
    if (res.status === HttpStatus.ok) {
      pageRouter.updateParams();
      setIsStatusChanging(false);
      listApi.execute((b) => b);
    } else {
      setIsStatusChanging(false);
      addLog({ error: res.message });
    }
  }
  const handleSubmit = async (data: CancellationForm) => {
    setIsStatusChanging(true);
    if (pageRouter.params.action === 'cancellation-restricted') {
      const response = await contractSetStatusApi(parseInt(pageRouter.params.id), data);
      onExecuted(response);
    }
    if (pageRouter.params.action === 'cancellation-unrestricted') {
      const response = await contractSetStatusUnrestrictedApi(parseInt(pageRouter.params.id), data);
      onExecuted(response);
    }
  };

  return (
    <ModalForm<CancellationForm>
      isQuery={true}
      subcomponents={ThemeModalFormDefault}
      dependencyArray={[selected]}
      title={lang.refundProcess}
      onCancel={pageRouter.updateParams}
      onSubmit={handleSubmit}
      default={{ newStatus: ContractStatusCode.CANCELED_REFUND_PR_REFUND }}
      validation={validateForm}
      tabs={CancellationTabs}
      render={({ activeTab }) => {
        useEffect(() => {
          if (activeTab) setCurrentTab(activeTab.id as Cancellation);
        }, [activeTab]);

        return activeTab?.id === Cancellation.REASON ? (
          <ContractCancellationReason />
        ) : activeTab?.id === Cancellation.REFUND ? (
          <ContractCancellationRefund />
        ) : null;
      }}
    />
  );
}
